import * as React from 'react'
import * as R from 'ramda'
import * as ThemeUI from 'theme-ui'
import * as PropTypes from 'prop-types'

import * as Icons from './icons'
import { currencySymbols } from './amount'

export function Input(props) {
  const [showPassword, setShowPassword] = React.useState(false)
  const inputRef = React.useRef(null)
  const [focused, setFocused] = React.useState(props.autoFocus)

  const value = props.value && props.value.toString()
  const valid = props.valid && !props.suppressVisualFeedback
  const invalid =
    props.invalid &&
    !props.valid &&
    props.visited &&
    !props.suppressVisualFeedback

  function handleBlur() {
    setFocused(false)
    if (props.onBlur) {
      props.onBlur()
    }
  }

  function handleFocus() {
    setFocused(true)
    if (props.onFocus) {
      props.onFocus()
    }
  }

  React.useEffect(() => {
    if (focused && inputRef.current) {
      inputRef.current.focus()
    }
  }, [focused, inputRef])

  return (
    <ThemeUI.Box
      as="label"
      sx={{
        'alignItems': 'center',
        'display': 'flex',
        'color': borderColor({ valid, invalid }),
        'backgroundColor': props.disabled
          ? 'input-disabled'
          : 'input-background',
        'border': '1px solid',
        'borderColor': borderColor({
          valid,
          invalid,
          disabled: props.disabled,
        }),
        'borderRadius': 1,
        'fontSize': 'md',
        'flexGrow': 1,
        'flexShrink': 1,
        'overflow': 'hidden',
        'position': 'relative',

        '&:hover': {
          borderColor: !props.disabled && hover({ valid, invalid }),
        },
        '&:disabled': {
          cursor: 'not-allowed',
          color: 'input-text-disabled',
        },
      }}
      ref={inputRef}
    >
      {props.prependIcon && (
        <IconWrapper xPosition="left">{props.prependIcon}</IconWrapper>
      )}

      {props.currency && (
        <IconWrapper
          sx={{
            color: 'disabled',
            fontWeight: '600',
            left: 1,
          }}
        >
          {currencySymbols[props.currency]}
        </IconWrapper>
      )}

      <ThemeUI.Input
        autoFocus={props.autoFocus}
        sx={{
          paddingLeft:
            props.currency && ['AUD', 'NZD', 'CAD'].includes(props.currency)
              ? '3em'
              : props.currency || props.prependIcon
              ? '1.5em'
              : '0.5em',
          paddingRight:
            props.hasStatus || props.appendIcon || valid || invalid
              ? props.type === 'password'
                ? '4em'
                : '2em'
              : '1em',
        }}
        autoComplete={props.autoComplete}
        autoCorrect={props.autoCorrect}
        disabled={props.disabled}
        id={props.id}
        inputMode={props.inputMode}
        maxLength={props.maxLength}
        minLength={props.minLength}
        prependIcon={props.prependIcon || props.currency}
        placeholder={props.placeholder}
        type={showPassword ? 'text' : props.type}
        value={value ?? ''}
        hasStatus={valid || invalid}
        onBlur={handleBlur}
        onChange={props.onChange}
        onFocus={handleFocus}
      />

      {props.appendIcon && <IconWrapper>{props.appendIcon}</IconWrapper>}

      {(props.type === 'password' || showPassword) && (
        <ThemeUI.IconButton
          type="button"
          onClick={() => setShowPassword(R.not)}
          sx={{
            position: 'absolute',
            right: valid || invalid || props.appendIcon ? 3 : 1,
            p: '0px',
          }}
        >
          <IconWrapper>
            <Icons.ShowPassword />
          </IconWrapper>
        </ThemeUI.IconButton>
      )}

      {valid && !props.disabled && (
        <IconWrapper color="success">
          <Icons.CheckCircle />
        </IconWrapper>
      )}

      {invalid && (
        <IconWrapper color="danger">
          <Icons.Error />
        </IconWrapper>
      )}
    </ThemeUI.Box>
  )
}

Input.propTypes = {
  appendIcon: PropTypes.func,
  autoComplete: PropTypes.string,
  autoCorrect: PropTypes.oneOf(['on', 'off']),
  autoFocus: PropTypes.bool,
  // TODO: create amountInput that passes correct icon instead of relying on currency
  currency: PropTypes.any,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  inputMode: PropTypes.oneOf(['numeric', 'email', 'decimal', 'tel']),
  invalid: PropTypes.bool,
  maxLength: PropTypes.string,
  minLength: PropTypes.string,
  name: PropTypes.string,
  prependIcon: PropTypes.element,
  placeholder: PropTypes.string,
  suppressVisualFeedback: PropTypes.bool,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  visited: PropTypes.bool,
  valid: PropTypes.bool,
  hasStatus: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
}

function IconWrapper(props) {
  return (
    <ThemeUI.Box
      sx={{
        position: 'absolute',
        [props.xPosition]: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        ...(props.onClick && { cursor: 'pointer' }),
        color: props.color || 'currentColor',
        display: 'inline-flex',
        ...props.sx,
      }}
    >
      {props.children}
    </ThemeUI.Box>
  )
}

IconWrapper.propTypes = {
  children: PropTypes.node,
  color: PropTypes.string,
  xPosition: PropTypes.oneOf(['left', 'right']),
  onClick: PropTypes.func,
  sx: PropTypes.object,
}

IconWrapper.defaultProps = {
  xPosition: 'right',
}

function borderColor(props) {
  if (props.disabled) {
    return 'input-border'
  } else if (props.valid) {
    return 'success'
  } else if (props.invalid) {
    return 'danger'
  } else {
    return 'input-border'
  }
}

function hover(props) {
  if (props.valid || props.invalid || props.disabled || props.focused) {
    return null
  } else {
    return 'input-hover'
  }
}
